"use client";

import { useLocale } from "next-intl";
import { useEffect, useState, useCallback, useMemo } from "react";
import Image from "next/image";
import { commonService } from "@/services/commonService";
import { cdnFlagsPath } from "@/constants/cdnUrls";
import CustomSelect from "./CustomSelect";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";

/**
 * CountrySelect Component
 *
 * Ülke seçimi için dinamik Select component'i
 * Countries verisini kendisi çeker ve Select component'ini kullanır
 *
 * @param {Object} props
 * @param {string} props.label - Label metni
 * @param {string} props.value - Seçili ülke kodu (controlled)
 * @param {Function} props.onChange - onChange callback: (countryCode, countryName, fullCountryData) => void
 * @param {string} props.defaultValue - Varsayılan ülke kodu (uncontrolled)
 * @param {string} props.error - Hata mesajı
 * @param {boolean} props.required - Zorunlu mu?
 * @param {string} props.className - Ek CSS class'ları
 * @param {string} props.placeholder - Placeholder metni
 * @param {boolean} props.disabled - Disabled durumu
 * @param {string} props.id - Input ID
 * @param {string} props.name - Input name
 * @param {boolean} props.showPlaceholder - Placeholder gösterilsin mi?
 */
export default function CountrySelect({
  label,
  value: controlledValue,
  onChange: propOnChange,
  defaultValue,
  error,
  required = false,
  className = "",
  placeholder = "Ülke seçiniz",
  disabled = false,
  id,
  name,
  showPlaceholder = true,
  removeTurkey = false,
  exclude = [],
  ...props
}) {
  const t = useTranslationDetector();
  const locale = useLocale();
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchCountries = useCallback(async () => {
    try {
      setIsLoading(true);
      // API isteği için exclude parametresini virgülle birleştirerek hazırla
      const excludeParam =
        Array.isArray(exclude) && exclude.length > 0
          ? exclude
              .map((code) => String(code).toLowerCase().trim())
              .filter(Boolean)
              .join(",")
          : undefined;

      const payload = excludeParam ? { exclude: excludeParam } : {};

      const response = await commonService.getCountriesNew(payload);

      if (response?.LocalizedCountries) {
        const formattedCountries = Array.isArray(response.LocalizedCountries)
          ? response.LocalizedCountries.filter(
              (item) => item.LanguageCode === locale.toUpperCase(),
            ).map((item) => ({
              value: item.CountryCode,
              label: item.CountryName,
              // Full country data'yı da sakla
              countryData: {
                code: item.CountryCode,
                name: item.CountryName,
                ...item, // Diğer tüm alanları da ekle
              },
            }))
          : [];
        setCountries(formattedCountries);
      }
    } catch (e) {
      console.error("Error fetching countries:", e);
      setCountries([]);
    } finally {
      setIsLoading(false);
    }
  }, [locale]);

  useEffect(() => {
    if (countries?.length > 0) {
      return;
    }

    fetchCountries();
  }, [countries, fetchCountries]);

  // defaultValue set edilmişse ve henüz bir değer seçilmemişse o ülkeyi otomatik seç
  useEffect(() => {
    if (!defaultValue) return;
    if (!Array.isArray(countries) || countries.length === 0) return;
    if (controlledValue) return;
    if (!propOnChange) return;

    const targetCode = String(defaultValue).toUpperCase().trim();

    const targetCountry = countries.find((country) => {
      const code = (country.value || country.countryData?.code || "")
        .toUpperCase()
        .trim();
      return code === targetCode;
    });

    if (targetCountry) {
      propOnChange(
        targetCountry.countryData.code,
        targetCountry.countryData.name,
        targetCountry.countryData,
      );
    }
  }, [defaultValue, countries, controlledValue, propOnChange]);

  // onChange handler - country code, name ve full data'yı döndür
  const handleChange = useCallback(
    (e) => {
      const selectedCode = e.target.value;
      const selectedCountry = countries.find(
        (country) => country.value === selectedCode,
      );

      if (propOnChange && selectedCountry) {
        // onChange'e code, name ve full data'yı gönder
        propOnChange(
          selectedCountry.countryData.code,
          selectedCountry.countryData.name,
          selectedCountry.countryData,
        );
      }
    },
    [countries, propOnChange],
  );

  // Custom option renderer - bayrak ikonu ve ülke adı göster
  const renderOption = useCallback(
    (option) => (
      <div className="flex items-center gap-2">
        <Image
          src={`${cdnFlagsPath}${option.value.toLowerCase()}.svg`}
          alt={option.label}
          width={20}
          height={20}
        />
        <span className="font-medium">{option.label}</span>
      </div>
    ),
    [],
  );

  // Select için options formatı
  const options = useMemo(() => {
    let filteredCountries = [...countries];

    const excludeSet = new Set(
      (Array.isArray(exclude) ? exclude : [])
        .map((code) => String(code).toUpperCase().trim())
        .filter(Boolean),
    );

    filteredCountries = filteredCountries.filter((country) => {
      const countryCode = (country.value || country.countryData?.code || "")
        .toUpperCase()
        .trim();

      if (!countryCode) return false;

      // Eski removeTurkey prop'u ile TR'yi gizle
      if (removeTurkey && countryCode === "TR") {
        return false;
      }

      // Yeni exclude listesi ile birden fazla ülkeyi gizle
      if (excludeSet.has(countryCode)) {
        return false;
      }

      return true;
    });

    return filteredCountries.map((country) => ({
      value: country.value,
      label: country.label,
    }));
  }, [countries, removeTurkey, exclude]);
  return (
    <CustomSelect
      label={label}
      value={controlledValue}
      onChange={handleChange}
      options={options}
      error={error}
      required={required}
      className={className}
      placeholder={t("common.selectCountry")}
      disabled={disabled || isLoading}
      id={id}
      name={name}
      renderOption={renderOption}
      {...props}
    />
  );
}
